import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSuperAdminAuth } from '../../../contexts/SuperAdminAuthContext';
import { useSuperAdminTheme } from '../../../contexts/SuperAdminThemeContext';
import { PendingApprovalsProvider } from '../../../contexts/PendingApprovalsContext';
import Sidebar from './Sidebar';
import VendorHealthStrip from '../components/VendorHealthStrip';
import { CommandPalette } from '../design';
import '../design/tokens.css';

/**
 * AdminLayout - Governance UI Layout
 * 
 * USER HIERARCHY:
 * - Governor (role="governor") - CAN access this layout
 * - Superadmin (role="superadmin") - CANNOT access this layout
 * - User (role="user") - CANNOT access this layout
 * 
 * USES: SuperAdminAuthContext (NOT regular AuthContext)
 * USES: SuperAdminThemeContext (NOT CRM theme)
 * REDIRECTS TO: /superadmin/login (NOT /login)
 */
export default function Layout({ children }) {
  const { user, loading, isAuthenticated } = useSuperAdminAuth();
  const { theme, isDark } = useSuperAdminTheme();
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    // Wait for auth to resolve before checking
    if (!loading) {
      if (!isAuthenticated) {
        console.log('[AdminLayout] Not authenticated, redirecting to Governor login');
        navigate('/superadmin/login', { replace: true });
        return;
      }
      
      // Check for governor role (Lockene internal only)
      const isGovernor = user?.role === 'governor' || user?.is_governor === true;
      if (!isGovernor) {
        console.log('[AdminLayout] User does not have governor role:', user?.role);
        navigate('/superadmin/login', { replace: true });
        return;
      }

      // Verify governance scope
      if (user?.scope !== 'governance') {
        console.log('[AdminLayout] Invalid token scope');
        navigate('/superadmin/login', { replace: true });
        return;
      }
      
      setAuthChecked(true);
    }
  }, [loading, isAuthenticated, user, navigate]);

  // Show loading while auth is being checked
  if (loading || !authChecked) {
    return (
      <div 
        className={`flex h-screen items-center justify-center ${isDark ? 'bg-slate-950' : 'bg-slate-100'}`}
        data-theme={theme}
      >
        <div className="text-center">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto mb-4`}></div>
          <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>Loading governance dashboard...</p>
        </div>
      </div>
    );
  }

  // Only render after auth is confirmed
  return (
    <PendingApprovalsProvider>
      <div
        className="gov-shell flex h-screen"
        data-theme={isDark ? 'gov' : 'gov-light'}
        data-testid="governance-layout"
      >
        <Sidebar />
        <main className="flex-1 overflow-auto">
          <VendorHealthStrip />
          <div className="mx-auto max-w-[1440px] p-6">
            {children}
          </div>
        </main>
        {/* Global ⌘K command palette */}
        <CommandPalette />
      </div>
    </PendingApprovalsProvider>
  );
}
